import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { api, img } from "../lib/api";

export default function BannerCarousel({ fallback }) {
  const [items, setItems] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const swRef = useRef({ x: 0, y: 0 });
  useEffect(() => { api.get("/banners").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => {
    if (!items?.length || paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 5500);
    return () => clearInterval(t);
  }, [items, paused]);
  if (items === null) return <div className="h-[420px] sm:h-[520px]" />;
  if (!items.length) return fallback;
  const go = (d) => setI((v) => (v + d + items.length) % items.length);
  const sw = swRef.current;

  return (
    <section data-testid="banner-carousel" className="relative h-[420px] sm:h-[520px] overflow-hidden touch-pan-y" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { sw.x = e.touches[0].clientX; sw.y = e.touches[0].clientY; setPaused(true); }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - sw.x, dy = e.changedTouches[0].clientY - sw.y; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1); setPaused(false); }}
      onPointerDown={(e) => { if (e.pointerType === "mouse") sw.x = e.clientX; }}
      onPointerUp={(e) => { if (e.pointerType === "mouse" && Math.abs(e.clientX - sw.x) > 60) go(e.clientX < sw.x ? 1 : -1); }}>
      <style>{`@keyframes kenburns{from{transform:scale(1.02)}to{transform:scale(1.14)}} @keyframes slideIn{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:none}}`}</style>
      {items.map((b, n) => (
        <div key={b.id} data-testid={`banner-slide-${n}`} aria-hidden={n !== i} className={`absolute inset-0 transition-opacity duration-1000 ${n === i ? "opacity-100 z-[1]" : "opacity-0"}`}>
          <img src={img(b.image)} alt={b.title} className="absolute inset-0 w-full h-full object-cover" style={n === i ? { animation: "kenburns 7s ease-out forwards" } : undefined} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090a0f] via-[#090a0f]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090a0f] via-transparent to-transparent" />
          {n === i && (
            <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col justify-center gap-5">
              <span className="w-fit text-xs font-bold uppercase tracking-[0.25em] px-3 py-1.5 rounded-full" style={{ background: `${b.accent}26`, color: b.accent, animation: "slideIn .6s .1s both" }}>Limited-time offer</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-2xl leading-[1.02]" style={{ animation: "slideIn .7s .2s both" }}>{b.title}</h1>
              {b.subtitle && <p className="text-slate-300 text-sm sm:text-lg max-w-lg" style={{ animation: "slideIn .7s .35s both" }}>{b.subtitle}</p>}
              <Link to={b.link || "/products"} data-testid={`banner-cta-${n}`} className="w-fit inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105" style={{ background: b.accent, boxShadow: `0 12px 34px -10px ${b.accent}`, animation: "slideIn .7s .5s both" }}>
                {b.cta_text || "Shop now"}<ArrowRight size={18} />
              </Link>
            </div>
          )}
        </div>
      ))}
      {items.length > 1 && (
        <>
          <button data-testid="banner-prev" onClick={() => go(-1)} className="hidden sm:block absolute left-3 top-1/2 -translate-y-1/2 z-[2] p-2 rounded-full bg-black/40 backdrop-blur hover:bg-black/70 transition-colors"><ChevronLeft /></button>
          <button data-testid="banner-next" onClick={() => go(1)} className="hidden sm:block absolute right-3 top-1/2 -translate-y-1/2 z-[2] p-2 rounded-full bg-black/40 backdrop-blur hover:bg-black/70 transition-colors"><ChevronRight /></button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[2] flex gap-2">
            {items.map((b, n) => <button key={b.id} data-testid={`banner-dot-${n}`} onClick={() => setI(n)} aria-label={`Slide ${n + 1}`} className={`h-2 rounded-full transition-[width,background-color] duration-500 ${n === i ? "w-8 bg-white" : "w-2 bg-white/40"}`} />)}
          </div>
        </>
      )}
    </section>
  );
}
